import type { FiredAlert, AlertSeverity } from "../types/alerts";

interface AlertStripProps {
  toasts: FiredAlert[];
  onDismiss: (id: string) => void;
  onOpenDrawer: () => void;
}

const SEVERITY_ORDER: Record<AlertSeverity, number> = {
  critical: 0,
  warning: 1,
  info: 2,
};

const SEVERITY_LABELS: Record<AlertSeverity, string> = {
  critical: "CRIT",
  warning: "WARN",
  info: "INFO",
};

function formatTime(ts: number): string {
  const d = new Date(ts);
  return d.toLocaleTimeString([], {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
}

export function AlertStrip({ toasts, onDismiss, onOpenDrawer }: AlertStripProps) {
  const sorted = [...toasts].sort((a, b) => {
    const sevDiff = SEVERITY_ORDER[a.severity] - SEVERITY_ORDER[b.severity];
    if (sevDiff !== 0) return sevDiff;
    return b.timestamp - a.timestamp;
  });

  const hasCritical = sorted.some((t) => t.severity === "critical");

  return (
    <div
      className="alert-strip"
      role="region"
      aria-label="Active alerts"
      aria-live={hasCritical ? "assertive" : "polite"}
      aria-atomic="false"
      hidden={sorted.length === 0}
    >
      {sorted.slice(0, 5).map((toast) => (
        <div
          key={toast.id}
          className={`alert-strip__item alert-strip__item--${toast.severity}`}
        >
          <button
            className="alert-strip__body"
            onClick={onOpenDrawer}
            type="button"
          >
            <span className={`alert-strip__badge alert-strip__badge--${toast.severity}`}>
              {SEVERITY_LABELS[toast.severity]}
            </span>
            <span className="alert-strip__message">{toast.message}</span>
          </button>
          <span className="alert-strip__time">{formatTime(toast.timestamp)}</span>
          <button
            className="alert-strip__dismiss"
            onClick={() => onDismiss(toast.id)}
            aria-label="Dismiss alert"
            type="button"
          >
            ✕
          </button>
        </div>
      ))}
    </div>
  );
}
